import { Component, OnInit, OnDestroy } from '@angular/core';
import { Subscription } from 'rxjs';
import { SignalRService, SignalRMessage } from '../../core/signalr.service';
import { STANDALONE_COMPONENT_IMPORTS } from '../standalone-component-imports';

@Component({
    standalone: true,
    imports: STANDALONE_COMPONENT_IMPORTS,
    selector: 'pd-signalr-status',
  templateUrl: './signalr-status.component.html',
  styleUrls: ['./signalr-status.component.less']
})
export class SignalRStatusComponent implements OnInit, OnDestroy {
  connectionStatus = 'Disconnected';
  connectionStatusClass = 'status-disconnected';
  isConnecting = false;
  showMessages = true;
  messages: SignalRMessage[] = [];

  private subscriptions: Subscription[] = [];

  constructor(private signalRService: SignalRService) {}

  ngOnInit(): void {
    // Subscribe to connection state changes
    const connectionSub = this.signalRService.getConnectionState().subscribe(state => {
      this.connectionStatus = state;
      this.updateStatusClass(state);
      this.isConnecting = state === 'Connecting' || state === 'Reconnecting';
    });
    this.subscriptions.push(connectionSub);

    // Subscribe to messages
    const messagesSub = this.signalRService.getMessages().subscribe(messages => {
      this.messages = messages;
    });
    this.subscriptions.push(messagesSub);

    // Automatically connect on initialization (optional)
    // this.connect();
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(sub => sub.unsubscribe());
    // Note: Don't disconnect SignalR here - it should persist across components
  }

  async toggleConnection(): Promise<void> {
    if (this.signalRService.isConnected()) {
      await this.disconnect();
    } else {
      await this.connect();
    }
  }

  private async connect(): Promise<void> {
    this.isConnecting = true;
    this.connectionStatus = 'Connecting';
    this.updateStatusClass('Connecting');
    try {
      await this.signalRService.startConnection();
      console.log('🎉 Successfully connected to SignalR Hub');
      this.connectionStatus = 'Connected';
      this.updateStatusClass('Connected');
    } catch (error) {
      console.error('💥 Failed to connect to SignalR Hub:', error);      
      this.connectionStatus = 'Error';
      this.updateStatusClass('Error');
    } finally {
      this.isConnecting = false;
    }
  }

  private async disconnect(): Promise<void> {
    this.isConnecting = true;
    try {
      await this.signalRService.stopConnection();
      this.connectionStatus = 'Disconnected';
      this.updateStatusClass('Disconnected');
      console.log('🔌 Disconnected from SignalR Hub');
    } catch (error) {
      this.connectionStatus = 'Error';
      this.updateStatusClass('Error');
      console.error('💥 Error disconnecting from SignalR Hub:', error);
    }
    finally {
      this.isConnecting = false;
    }
  }

  private updateStatusClass(state: string): void {
    switch (state) {
      case 'Connected':
        this.connectionStatusClass = 'status-connected';
        break;
      case 'Connecting':
      case 'Reconnecting':
        this.connectionStatusClass = 'status-connecting';
        break;
      case 'Error':
        this.connectionStatusClass = 'status-error';
        break;
      case 'Disconnected':
        this.connectionStatusClass = 'status-disconnected';
        break;
      default:
        this.connectionStatusClass = 'status-disconnected';
        break;
    }
  }

  getConnectionButtonText(): string {
    if (this.isConnecting) {
      return 'Please wait...';
    }
    return this.signalRService.isConnected() ? 'Disconnect' : 'Connect';
  }

  clearMessages(): void {
    this.signalRService.clearMessages();
  }

  trackMessage(index: number, message: SignalRMessage): string {
    // Handle timestamp safely - it might be Date, string, or undefined
    let timeValue = Date.now();
    if (message.timestamp) {
      if (message.timestamp instanceof Date) {
        timeValue = message.timestamp.getTime();
      } else if (typeof message.timestamp === 'string') {
        timeValue = new Date(message.timestamp).getTime();
      }
    }
    return `${timeValue}_${message.name || index}`;
  }

  formatMessageData(message: SignalRMessage): string {
    try {
      // Simply display the raw data string
      if (message.data) {
        // Try to format JSON for better readability
        try {
          const parsed = JSON.parse(message.data);
          return JSON.stringify(parsed, null, 2);
        } catch {
          return message.data; // Return as-is if not JSON
        }
      }
      return 'N/A';
    } catch (error) {
      return `Error: ${error}`;
    }
  }

  /**
   * Format message in minimal format: "дата: имя сообщения: пэйлоад"
   */
  formatMessageMinimal(message: SignalRMessage): string {
    // Handle timestamp safely
    let time = 'N/A';
    if (message.timestamp) {
      try {
        const date = message.timestamp instanceof Date ? message.timestamp : new Date(message.timestamp);
        time = date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
      } catch (error) {
        time = 'N/A';
      }
    }
    
    const name = message.name || 'Unknown';
    
    // Show full payload instead of extracting specific fields
    let payload = 'N/A';
    if (message.data) {
      payload = message.data.length > 100 ? message.data.substring(0, 100) + '...' : message.data;
    }
    
    return `${time}: ${name}: ${payload}`;
  }

  /**
   * Get formatted time for message
   */
  getMessageTime(message: SignalRMessage): string {
    if (message.timestamp) {
      try {
        const date = message.timestamp instanceof Date ? 
          message.timestamp : 
          new Date(message.timestamp);
        return date.toLocaleTimeString('en-US', { 
          hour: '2-digit', 
          minute: '2-digit',
          second: '2-digit'
        });
      } catch (error) {
        return 'N/A';
      }
    }
    return 'N/A';
  }

  /**
   * Get message name
   */
  getMessageName(message: SignalRMessage): string {
    return message.name || 'Unknown';
  }

  /**
   * Get message payload
   */
  getMessagePayload(message: SignalRMessage): string {
    if (message.data) {
      // Shorter payload for inline display
      if (message.data.length > 40) {
        return message.data.substring(0, 40) + '...';
      }
      return message.data;
    }
    return 'N/A';
  }

  /**
   * Get full message for tooltip
   */
  getFullMessageTooltip(message: SignalRMessage): string {
    const time = this.getMessageTime(message);
    const name = this.getMessageName(message);
    const fullPayload = message.data || 'N/A';
    return `${time}: ${name}: ${fullPayload}`;
  }
}
